import {useEffect} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {
  AutoComplete,
  Col,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Modal,
} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import SubmitButton from 'components/Startup/components/SubmitButton';
import DBModal from 'components/modal/modal';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {GET_ALL_GROUPS} from 'graphql/queries/groups';
import {toast} from 'react-toastify';
import {walletCurrencies} from 'utils/mock';
import {UPSERT_SUBSCRIPTION_CATALOG_ITEM} from '../resolvers';
import styles from '../subscriptionSettings.module.scss';
import {normalizeSubscriptionType} from '../subscriptions/PlanSubscriptionsHelpers';
import CatalogLocalizedFieldsSection from './catalogItemModal/CatalogLocalizedFieldsSection';
import CatalogModalSection from './catalogItemModal/CatalogModalSection';
import CatalogPricingSection from './catalogItemModal/CatalogPricingSection';
import CatalogPurchaseSettingsSection from './catalogItemModal/CatalogPurchaseSettingsSection';

const PURCHASE_TYPES = [
  {value: 'ONE_OFF', label: 'One-off'},
  {value: 'TIME_BASED', label: 'Time based'},
];

const DURATION_UNITS = ['DAY', 'WEEK', 'MONTH', 'YEAR'].map(unit => ({
  value: unit,
  label: unit,
}));

const DISCOUNT_TYPES = [
  {value: 'NONE', label: 'None'},
  {value: 'PERCENTAGE', label: 'Percentage'},
  {value: 'FIXED', label: 'Fixed amount'},
];

const defaultPriceRow = () => ({
  currency: 'USD',
  amount: 0,
  discountType: 'NONE',
  discountValue: null,
});

const mapApiItemToForm = (item, catalogPlan) => ({
  key: item?.key ?? null,
  plan: catalogPlan ?? item?.plan ?? null,
  label: {
    EN: item?.label?.EN ?? '',
    FR: item?.label?.FR ?? '',
  },
  description: {
    EN: item?.description?.EN ?? '',
    FR: item?.description?.FR ?? '',
  },
  tag: {
    EN: item?.tag?.EN ?? '',
    FR: item?.tag?.FR ?? '',
  },
  purchaseType: item?.purchaseType ?? 'TIME_BASED',
  redirectUrl: item?.redirectUrl ?? '',
  durationUnit: item?.duration?.unit ?? 'YEAR',
  durationValue: item?.duration?.value ?? 1,
  allowCoupon: Boolean(item?.allowCoupon),
  allowRecurring: Boolean(item?.allowRecurring),
  defaultRecurring: Boolean(item?.defaultRecurring),
  appleProductId: item?.appleProductId ?? '',
  groupId: item?.groupId ?? '',
  isVisibleToUsers: item?.isVisibleToUsers ?? true,
  isEnabled: item?.isEnabled ?? true,
  prices: (item?.prices?.length ? item.prices : [defaultPriceRow()]).map(
    price => ({
      currency: price?.currency,
      amount: price?.amount ?? 0,
      discountType: price?.discount?.type ?? 'NONE',
      discountValue:
        price?.discount?.type === 'NONE' ||
        price?.discount?.value === undefined ||
        price?.discount?.value === null
          ? null
          : Number(price.discount.value),
    }),
  ),
});

const buildCatalogItemPayload = (values, catalogPlan) => {
  const purchaseType = values.purchaseType;
  const normalizedSubscriptionType = normalizeSubscriptionType(
    values.subscriptionType,
  );

  // const subscriptionTypeValue = values.subscriptionType?.trim();

  return {
    key: values.key || normalizedSubscriptionType,
    label: {EN: values.label.EN, FR: values.label.FR},
    description: {EN: values.description.EN, FR: values.description.FR},
    tag: {EN: values.tag.EN, FR: values.tag.FR},
    plan: catalogPlan ?? null,
    // ...(subscriptionTypeValue && {subscriptionType: subscriptionTypeValue}),
    prices: values.prices.map(price => {
      const discountType = price?.discountType ?? 'NONE';
      const discountValue = price?.discountValue;
      const hasDiscount =
        discountType !== 'NONE' &&
        discountValue !== null &&
        discountValue !== undefined &&
        `${discountValue}`.trim() !== '';

      return {
        currency: price.currency,
        amount: Number(price.amount),
        ...(hasDiscount && {
          discount: {
            type: discountType,
            value: Number(discountValue),
          },
        }),
      };
    }),
    allowCoupon: values.allowCoupon,
    appleProductId: values.appleProductId?.trim() || null,
    purchaseType,
    redirectUrl:
      purchaseType === 'ONE_OFF' ? values.redirectUrl?.trim() || null : null,
    duration:
      purchaseType === 'TIME_BASED'
        ? {
            unit: values.durationUnit,
            value: Number(values.durationValue),
          }
        : null,
    allowRecurring: purchaseType === 'ONE_OFF' ? false : values.allowRecurring,
    defaultRecurring:
      purchaseType === 'ONE_OFF' ? false : values.defaultRecurring,
    groupId: values.groupId?.trim() || null,
    isVisibleToUsers: values.isVisibleToUsers,
    isEnabled: values.isEnabled,
  };
};

const SubscriptionCatalogItemModal = ({
  isOpen,
  onClose,
  planKey,
  catalogPlan,
  subscriptionTypeKeys = [],
  editContext,
  refetch,
  canSubmit,
}) => {
  const [form] = Form.useForm();
  const isEditMode = Boolean(editContext?.item?.key);
  const purchaseType = Form.useWatch('purchaseType', form);

  const currencyOptions = walletCurrencies.map(currency => ({
    value: currency.key,
    label: `${currency.key} (${currency.symbol})`,
  }));

  const [upsert, {loading}] = useMutation(UPSERT_SUBSCRIPTION_CATALOG_ITEM, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const {data: groupsData} = useQuery(GET_ALL_GROUPS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_TEXT,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (editContext?.item) {
      form.setFieldsValue(mapApiItemToForm(editContext.item, catalogPlan));
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      key: '',
      plan: catalogPlan ?? null,
      subscriptionType: '',
      label: {EN: '', FR: ''},
      description: {EN: '', FR: ''},
      tag: {EN: '', FR: ''},
      purchaseType: 'TIME_BASED',
      redirectUrl: '',
      durationUnit: 'YEAR',
      durationValue: 1,
      allowCoupon: false,
      allowRecurring: true,
      defaultRecurring: true,
      appleProductId: '',
      groupId: '',
      isVisibleToUsers: true,
      isEnabled: true,
      prices: [defaultPriceRow()],
    });
  }, [isOpen, editContext, catalogPlan, form]);

  useEffect(() => {
    if (purchaseType === 'ONE_OFF') {
      form.setFieldsValue({
        allowRecurring: false,
        defaultRecurring: false,
      });
    }
  }, [purchaseType, form]);

  const groupsResponse = groupsData?.getAllGroups;
  useEffect(() => {
    if (groupsResponse?.__typename === 'Error') {
      toast.error(groupsResponse.message);
    }
  }, [groupsResponse]);

  const groups =
    groupsResponse?.__typename === 'GroupList'
      ? groupsResponse.groups || []
      : [];

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = values => {
    Modal.confirm({
      title: isEditMode
        ? 'Are you sure you want to update this catalog item?'
        : 'Are you sure you want to create this catalog item?',
      content:
        'Please ensure that all pricing and visibility configurations are correct before proceeding.',
      okText: isEditMode ? 'Update' : 'Create',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => {
        const catalogItem = buildCatalogItemPayload(values, catalogPlan);

        return upsert({
          variables: {
            input: catalogItem,
          },
        })
          .then(({data}) => {
            const response = data?.upsertSubscriptionCatalogItem;
            if (response?.__typename === 'Error') {
              toast.error(response.message);
              return;
            }

            toast.success(response?.message || 'Catalog item saved');
            handleClose();
            if (refetch) {
              refetch();
            }
          })
          .catch(error => {
            toast.error(error.message || 'Save failed');
          });
      },
    });
  };

  const handleTranslate = fieldName => {
    const sourceText = form.getFieldValue([fieldName, 'EN'])?.trim();
    if (!sourceText) {
      return;
    }

    translateText({
      variables: {
        sourceLang: 'EN',
        targetLang: 'FR',
        sourceText,
      },
    })
      .then(({data}) => {
        const response = data?.translateText;
        if (
          response?.statusCode === 400 ||
          response?.__typename === 'Error' ||
          !response?.targetText
        ) {
          toast.error(response?.message || 'Error Translating Text');
          return;
        }

        form.setFieldValue([fieldName, 'FR'], response.targetText);
        toast.success('Translated Successfully');
      })
      .catch(error => {
        toast.error(error.message || 'Error Translating Text');
      });
  };

  const localeRequired = [{required: true, message: 'Required'}];

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={handleClose}
      destroyOnClose
      width={980}
      content={
        <div className={styles['catalog-modal']}>
          <div className={styles['catalog-modal-header']}>
            <div>
              <h1>
                {isEditMode
                  ? 'Edit Subscription Item'
                  : 'Create Subscription Item'}
              </h1>
              <p>
                For Plan: <code>{planKey?.replace(/_/g, ' ')}</code>
              </p>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            className={styles['catalog-modal-form']}
            onFinish={handleSubmit}
            onFinishFailed={() => toast.error('Fix validation errors')}>
            <Form.Item name="key" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="plan" hidden>
              <Input />
            </Form.Item>

            {!isEditMode ? (
              <CatalogModalSection title="Subscription Type">
                <Form.Item
                  label={
                    <span>
                      Subscription Name (Internal)&nbsp;
                      <Tooltip title="This is the underlying system identifer for the plan duration (e.g. One Month Daba Subscription). Users won't see this directly.">
                        <InfoCircleOutlined style={{color: '#8b82a7'}} />
                      </Tooltip>
                    </span>
                  }
                  name="subscriptionType"
                  rules={[
                    {required: true, message: 'Subscription name required'},
                  ]}>
                  <AutoComplete
                    style={{width: '100%'}}
                    options={subscriptionTypeKeys.map(key => ({
                      value: key,
                      label: key,
                    }))}
                    placeholder="e.g. One Year"
                    filterOption={(inputValue, option) =>
                      (option?.value ?? '')
                        .toUpperCase()
                        .includes(inputValue.toUpperCase())
                    }
                  />
                </Form.Item>
              </CatalogModalSection>
            ) : null}

            <CatalogModalSection
              title="Display Headers & Descriptions"
              description="User-facing text copy localized for English and French.">
              <CatalogLocalizedFieldsSection
                title="Label (Title)"
                name="label"
                requiredRules={localeRequired}
                enableTranslate
                loadingTranslation={loadingTranslation}
                onTranslate={handleTranslate}
              />
              <CatalogLocalizedFieldsSection
                title="Description"
                name="description"
                requiredRules={localeRequired}
                multiline
                enableTranslate
                loadingTranslation={loadingTranslation}
                onTranslate={handleTranslate}
              />
              <CatalogLocalizedFieldsSection
                title="Tag (Optional)"
                name="tag"
                requiredRules={[]}
                enableTranslate
                loadingTranslation={loadingTranslation}
                onTranslate={handleTranslate}
              />
            </CatalogModalSection>

            <CatalogPurchaseSettingsSection
              purchaseType={purchaseType}
              purchaseTypes={PURCHASE_TYPES}
              durationUnits={DURATION_UNITS}
            />

            <CatalogPricingSection
              discountTypes={DISCOUNT_TYPES}
              currencyOptions={currencyOptions}
            />

            <CatalogModalSection
              title="Optional integrations"
              description="Optional IDs for native app store and user segmentation.">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span>
                        Apple product ID&nbsp;
                        <Tooltip title="If you created a product in AppStore Connect, enter the ID here.">
                          <InfoCircleOutlined style={{color: '#8b82a7'}} />
                        </Tooltip>
                      </span>
                    }
                    name="appleProductId">
                    <Input placeholder="e.g. com.daba.pro.monthly" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span>
                        Target User Group&nbsp;
                        <Tooltip title="Once users are subscribed to this plan it takes them to this particular group">
                          <InfoCircleOutlined style={{color: '#8b82a7'}} />
                        </Tooltip>
                      </span>
                    }
                    name="groupId">
                    <Select
                      showSearch
                      placeholder="Select a Group"
                      optionFilterProp="children"
                      filterOption={(inputValue, option) =>
                        (option?.children || '')
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }>
                      {groups.map(group => (
                        <Select.Option key={group?.id} value={group?.id}>
                          {group?.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </CatalogModalSection>

            <div className={styles['catalog-modal-actions']}>
              <SubmitButton
                disabled={loading || !canSubmit}
                label={isEditMode ? 'Save' : 'Create'}
              />
            </div>
          </Form>
        </div>
      }
    />
  );
};

export default SubscriptionCatalogItemModal;
